/**
 * Notification Preferences Tab Component
 * 
 * Allows users to manage their email notification preferences.
 */

import React, { useState, useEffect } from 'react';
import { Bell, Mail, MailX, RefreshCw, Clock, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import * as notificationsApi from '../../api/notifications';

const NotificationPreferencesTab = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preferences, setPreferences] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    try {
      setLoading(true);
      const response = await notificationsApi.getNotificationPreferences();
      if (response.success) {
        setPreferences(response.data);
      }
    } catch (error) {
      console.error('Error loading preferences:', error);
      toast.error('Failed to load notification preferences');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (category, field) => {
    if (!preferences) return;

    const newPreferences = { ...preferences };
    
    if (category === 'email_enabled') {
      newPreferences.email_enabled = !newPreferences.email_enabled;
    } else if (category === 'workflow_notifications') {
      newPreferences.workflow_notifications = {
        ...newPreferences.workflow_notifications,
        [field]: !newPreferences.workflow_notifications[field]
      };
    } else if (category === 'digest') {
      newPreferences.digest = {
        ...newPreferences.digest,
        [field]: !newPreferences.digest[field]
      };
    }

    setPreferences(newPreferences);
    setHasChanges(true);
  };

  const savePreferences = async () => {
    if (!preferences) return;

    try {
      setSaving(true);
      
      // Flatten the preferences structure for the API
      const updates = {
        email_enabled: preferences.email_enabled,
        notify_task_assigned: preferences.workflow_notifications?.task_assigned,
        notify_step_completed: preferences.workflow_notifications?.step_completed,
        notify_workflow_completed: preferences.workflow_notifications?.workflow_completed,
        notify_workflow_cancelled: preferences.workflow_notifications?.workflow_cancelled,
        notify_stale_workflows: preferences.workflow_notifications?.stale_workflows,
        daily_digest: preferences.digest?.daily,
        weekly_digest: preferences.digest?.weekly
      };

      const response = await notificationsApi.updateNotificationPreferences(updates);
      if (response.success) {
        toast.success('Preferences saved successfully');
        setHasChanges(false);
      }
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = async () => {
    try {
      setSaving(true);
      const response = await notificationsApi.resetNotificationPreferences();
      if (response.success) {
        setPreferences(response.data);
        setHasChanges(false);
        toast.success('Preferences reset to defaults');
      }
    } catch (error) {
      console.error('Error resetting preferences:', error);
      toast.error('Failed to reset preferences');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!preferences) {
    return (
      <Card className="border-red-200 bg-red-50">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 text-red-700">
            <AlertCircle className="w-5 h-5" />
            <span>Failed to load notification preferences</span>
          </div>
          <Button onClick={loadPreferences} className="mt-4" variant="outline">
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  const emailEnabled = preferences.email_enabled;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="notification-preferences-tab">
      {/* Global Email Toggle */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5" />
            Email Notifications
          </CardTitle>
          <CardDescription>
            Enable or disable all email notifications from AutoFlo
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {emailEnabled ? (
                <div className="p-2 bg-green-100 rounded-lg">
                  <Mail className="w-5 h-5 text-green-600" />
                </div>
              ) : (
                <div className="p-2 bg-gray-100 rounded-lg">
                  <MailX className="w-5 h-5 text-gray-500" />
                </div>
              )}
              <div>
                <p className="font-medium text-gray-900">
                  {emailEnabled ? 'Emails Enabled' : 'Emails Disabled'}
                </p>
                <p className="text-sm text-gray-500">
                  {emailEnabled 
                    ? 'You will receive email notifications based on your preferences below'
                    : 'You will not receive any email notifications'}
                </p>
              </div>
            </div>
            <Switch
              checked={emailEnabled}
              onCheckedChange={() => handleToggle('email_enabled')}
              data-testid="email-enabled-toggle"
            />
          </div>
        </CardContent>
      </Card>

      {/* Workflow Notifications */}
      <Card className={!emailEnabled ? 'opacity-50 pointer-events-none' : ''}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5" />
            Workflow Notifications
          </CardTitle>
          <CardDescription>
            Choose which workflow events you want to be notified about
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <NotificationItem
            label="Task Assigned"
            description="When a new workflow task is assigned to you"
            checked={preferences.workflow_notifications?.task_assigned ?? true}
            onChange={() => handleToggle('workflow_notifications', 'task_assigned')}
            testId="notify-task-assigned"
            icon={<CheckCircle2 className="w-4 h-4 text-blue-500" />}
          />
          
          <NotificationItem
            label="Step Completed"
            description="When a step in a workflow you're involved in is completed"
            checked={preferences.workflow_notifications?.step_completed ?? true}
            onChange={() => handleToggle('workflow_notifications', 'step_completed')}
            testId="notify-step-completed"
            icon={<CheckCircle2 className="w-4 h-4 text-green-500" />}
          />
          
          <NotificationItem
            label="Workflow Completed"
            description="When an entire workflow is completed"
            checked={preferences.workflow_notifications?.workflow_completed ?? true}
            onChange={() => handleToggle('workflow_notifications', 'workflow_completed')}
            testId="notify-workflow-completed"
            icon={<CheckCircle2 className="w-4 h-4 text-green-600" />}
          />
          
          <NotificationItem
            label="Workflow Cancelled"
            description="When a workflow you're involved in is cancelled"
            checked={preferences.workflow_notifications?.workflow_cancelled ?? false}
            onChange={() => handleToggle('workflow_notifications', 'workflow_cancelled')}
            testId="notify-workflow-cancelled"
            icon={<AlertCircle className="w-4 h-4 text-red-500" />}
          />
          
          <NotificationItem
            label="Stale Workflow Alerts"
            description="When workflows haven't progressed within the expected timeframe (Admin)"
            checked={preferences.workflow_notifications?.stale_workflows ?? true}
            onChange={() => handleToggle('workflow_notifications', 'stale_workflows')}
            testId="notify-stale-workflows"
            icon={<Clock className="w-4 h-4 text-orange-500" />}
          />
        </CardContent>
      </Card>

      {/* Digest Preferences */}
      <Card className={!emailEnabled ? 'opacity-50 pointer-events-none' : ''}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Email Digest
          </CardTitle>
          <CardDescription>
            Receive summary emails instead of individual notifications
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <NotificationItem
            label="Daily Digest"
            description="Receive a daily summary of all workflow activity"
            checked={preferences.digest?.daily ?? false}
            onChange={() => handleToggle('digest', 'daily')}
            testId="daily-digest"
          />
          
          <NotificationItem
            label="Weekly Digest"
            description="Receive a weekly summary of workflow activity"
            checked={preferences.digest?.weekly ?? false}
            onChange={() => handleToggle('digest', 'weekly')}
            testId="weekly-digest"
          />
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex items-center justify-between pt-4">
        <Button
          variant="outline"
          onClick={resetToDefaults}
          disabled={saving}
          data-testid="reset-preferences-btn"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Reset to Defaults
        </Button>
        
        <Button
          onClick={savePreferences}
          disabled={!hasChanges || saving}
          data-testid="save-preferences-btn"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            'Save Preferences'
          )}
        </Button>
      </div>

      {hasChanges && (
        <p className="text-sm text-amber-600 text-center">
          You have unsaved changes
        </p>
      )}
    </div>
  );
};

const NotificationItem = ({ label, description, checked, onChange, testId, icon }) => {
  return (
    <div className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
      <div className="flex items-start gap-3">
        {icon && <div className="mt-0.5">{icon}</div>}
        <div>
          <Label className="font-medium text-gray-900">{label}</Label>
          <p className="text-sm text-gray-500">{description}</p>
        </div>
      </div>
      <Switch
        checked={checked}
        onCheckedChange={onChange}
        data-testid={testId}
      />
    </div>
  );
};

export default NotificationPreferencesTab;
